import React from 'react';
import { createPortal } from 'react-dom';
import { Tooltip, useAnchoredFlyout } from '@odo/shared-ui';
import { type WorkQueueGroup, type WorkQueueMeta } from '../../services/workQueue';
import { useStatusCatalog } from '../../services/statusCatalog';
import { IMPLEMENTED_PHASES, IMPLEMENTED_STATUSES } from './constants';
import { buildPhaseSections, type PhaseSection, type StepItem } from './groupNavSections';
import { PHASE_HEX, phaseTint } from '../../utils/phaseColors';
import { TeamSplitPill } from '@odo/shared-ui/components/TeamPill';

interface GroupNavProps {
    selectedKey: string | null;
    onSelect: (key: string) => void;
    groups: WorkQueueGroup[] | undefined;
    meta: WorkQueueMeta | undefined;
    phaseOpen: Record<string, boolean>;
    onTogglePhase: (phase: string) => void;
    filterActive?: boolean;
    folded?: boolean;
    onToggleFold?: () => void;
    teamFilter?: string[];
}

export function GroupNav({
    selectedKey,
    onSelect,
    groups,
    meta,
    phaseOpen,
    onTogglePhase,
    filterActive = false,
    folded = false,
    onToggleFold,
    teamFilter = []
}: GroupNavProps) {
    const sections = React.useMemo(
        () => buildPhaseSections(groups ?? [], meta?.statuses, meta?.phases, teamFilter),
        [groups, meta?.statuses, meta?.phases, teamFilter]
    );

    // Scroll the active step into view on selection change or first load, adjusting only the
    // tree container's scrollTop, never the window.
    const treeRef = React.useRef<HTMLDivElement | null>(null);
    const selectedRowRef = React.useRef<HTMLButtonElement | null>(null);
    React.useEffect(() => {
        if (folded || !selectedKey) return;
        const raf = requestAnimationFrame(() => {
            const container = treeRef.current;
            const row = selectedRowRef.current;
            if (!container || !row) return; // selected step's phase is collapsed/absent
            const cRect = container.getBoundingClientRect();
            const rRect = row.getBoundingClientRect();
            const delta = rRect.top - cRect.top - (container.clientHeight / 2 - rRect.height / 2);
            container.scrollTo({
                top: Math.max(0, container.scrollTop + delta),
                behavior: 'smooth'
            });
        });
        return () => cancelAnimationFrame(raf);
        // sections.length (not the array) so this fires on initial load + selection
        // change, but not on every 2-min auto-refresh (which would yank the scroll).
    }, [selectedKey, folded, sections.length]);

    // Which phase's flyout panel is open in rail mode - open/dismiss owned
    // by the shared hook (Escape, outside click, re-expand).
    const {
        flyout,
        toggle: togglePhaseFlyout,
        close: closePhaseFlyout,
        containerRef: railWrapperRef
    } = useAnchoredFlyout<HTMLDivElement>({
        enabled: folded,
        markerSelector: '[data-wq-flyout]'
    });
    const flyoutPhase = flyout?.key ?? null;

    if (folded) {
        return (
            <div ref={railWrapperRef} className="h-full w-full">
                <RailMode
                    sections={sections}
                    selectedKey={selectedKey}
                    flyoutPhase={flyoutPhase}
                    onPhaseClick={(phase) => togglePhaseFlyout(phase)}
                    onSelect={(key) => {
                        onSelect(key);
                        closePhaseFlyout();
                    }}
                    onToggleFold={onToggleFold}
                    filterActive={filterActive}
                />
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Fold-to-rail toggle. Sits in a thin header bar above the tree;
          rail-mode renders its own copy at the top of the rail. */}
            {onToggleFold && (
                <div className="flex items-center justify-end px-2 py-1 border-b border-gray-100 flex-shrink-0">
                    <button
                        type="button"
                        onClick={onToggleFold}
                        aria-label="Fold navigation to icon rail"
                        title="Fold to icon rail"
                        className="flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Phase tree - scrollbar hidden for cleaner look (matches files-viewer chrome) */}
            <div ref={treeRef} className="flex-1 overflow-y-auto scrollbar-hide">
                {sections.map((section, idx) => {
                    // With a filter/search active, open state follows match counts instead of the user's
                    // toggles; headers of phases without hits stay visible for context.
                    const isOpen = filterActive
                        ? section.totalCount > 0
                        : phaseOpen[section.phase] !== false;
                    const isLast = idx === sections.length - 1;
                    const isPhaseImplemented = IMPLEMENTED_PHASES.has(section.phase);
                    const phaseHasAnyImplemented =
                        isPhaseImplemented ||
                        section.items.some((item) => IMPLEMENTED_STATUSES.has(item.key));
                    // When filtering, hide steps with zero matches so the tree only
                    // surfaces what the user is looking for.
                    const visibleItems = filterActive
                        ? section.items.filter((item) => item.count > 0)
                        : section.items;

                    return (
                        <div
                            key={section.phase}
                            className={`${isLast ? '' : 'border-b border-gray-100'}`}
                        >
                            <button
                                onClick={() => {
                                    if (!filterActive) onTogglePhase(section.phase);
                                }}
                                disabled={filterActive}
                                className={`w-full flex items-center gap-2 pl-3 pr-2.5 py-2.5 select-none transition-colors border-l-[3px] ${
                                    filterActive ? 'cursor-default' : 'cursor-pointer'
                                } ${phaseHasAnyImplemented ? '' : 'opacity-50'}`}
                                style={{
                                    background: phaseTint(
                                        PHASE_HEX[section.phase],
                                        isOpen ? 0.12 : 0.07
                                    ),
                                    borderLeftColor: PHASE_HEX[section.phase] ?? 'transparent'
                                }}
                            >
                                <span
                                    className={`font-bold text-[12px] flex-1 text-left uppercase tracking-[0.06em] ${
                                        phaseHasAnyImplemented ? 'text-gray-800' : 'text-gray-500'
                                    }`}
                                >
                                    {section.phase}
                                </span>
                                <span className="text-[11px] font-medium text-gray-600">
                                    {section.totalCount.toLocaleString()}
                                </span>
                                <span
                                    className="text-[9px] text-gray-500 transition-transform duration-150 ml-0.5"
                                    style={{
                                        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                                        display: 'inline-block'
                                    }}
                                >
                                    ▶
                                </span>
                            </button>

                            {/* Status items - visually subordinate to the phase header.
                  Container carries a very faint phase tint so the section
                  reads as a unified block. */}
                            {isOpen && (
                                <div
                                    className="py-1"
                                    style={{
                                        background: phaseTint(PHASE_HEX[section.phase], 0.03)
                                    }}
                                >
                                    {visibleItems.map((item) => (
                                        <StepRow
                                            key={item.key}
                                            item={item}
                                            selected={selectedKey === item.key}
                                            rowRef={
                                                selectedKey === item.key
                                                    ? selectedRowRef
                                                    : undefined
                                            }
                                            // A step is "live" when its phase is implemented OR
                                            // the specific status is whitelisted (per-step override).
                                            implemented={
                                                isPhaseImplemented ||
                                                IMPLEMENTED_STATUSES.has(item.key)
                                            }
                                            onSelect={onSelect}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// Step row shared by the expanded tree and the rail flyout, so gating, selection,
// urgency dot and tooltip stay in lockstep.

interface StepRowProps {
    item: StepItem;
    selected: boolean;
    implemented: boolean;
    onSelect: (key: string) => void;
    // Set on the currently-selected row so the tree can scroll it into view.
    rowRef?: React.Ref<HTMLButtonElement>;
}

function StepRow({ item, selected, implemented, onSelect, rowRef }: StepRowProps) {
    const catalog = useStatusCatalog();
    const display = catalog.display(item.key);
    const tooltipText = implemented ? display : `${display} - not implemented yet`;

    return (
        <button
            type="button"
            ref={rowRef}
            onClick={() => onSelect(item.key)}
            className={`w-full flex items-center gap-1.5 pl-4 pr-3 py-1.5 select-none transition-colors text-left ${
                implemented ? 'cursor-pointer' : 'cursor-default opacity-50'
            } ${
                selected
                    ? 'bg-blue-50 border-r-2 border-blue-600'
                    : 'hover:bg-black/[0.03] border-r-2 border-transparent'
            }`}
        >
            <Tooltip content={tooltipText} side="right">
                <span
                    className={`flex-1 text-xs truncate ${
                        selected
                            ? 'font-semibold text-gray-800'
                            : implemented
                              ? 'font-normal text-gray-500'
                              : 'font-normal italic text-gray-400'
                    }`}
                >
                    {display}
                </span>
            </Tooltip>
            <TeamSplitPill teams={item.owners} />
            <span className="text-[11px] text-gray-400 flex-shrink-0">
                {item.count.toLocaleString()}
            </span>
        </button>
    );
}

// Rail mode (folded state)

interface RailModeProps {
    sections: PhaseSection[];
    selectedKey: string | null;
    flyoutPhase: string | null;
    onPhaseClick: (phase: string) => void;
    onSelect: (key: string) => void;
    onToggleFold?: () => void;
    filterActive: boolean;
}

// Compact 48px rail with one icon per phase and a flyout for an implemented phase, using
// the existing implementation-state model and palette.
function RailMode({
    sections,
    selectedKey,
    flyoutPhase,
    onPhaseClick,
    onSelect,
    onToggleFold,
    filterActive
}: RailModeProps) {
    const activePhase = flyoutPhase
        ? (sections.find((s) => s.phase === flyoutPhase) ?? null)
        : null;

    // Phase of the selected step, the active signal while no flyout is open; an open flyout
    // takes precedence so only one icon is ever highlighted.
    const selectedPhase = React.useMemo(() => {
        if (!selectedKey) return null;
        return sections.find((s) => s.items.some((i) => i.key === selectedKey))?.phase ?? null;
    }, [sections, selectedKey]);
    const activeRailPhase = activePhase?.phase ?? selectedPhase;

    // Rail's right edge in viewport coords, for anchoring the portalled flyout; recomputed on
    // resize, scroll and active-phase change.
    const railRef = React.useRef<HTMLDivElement | null>(null);
    const [anchor, setAnchor] = React.useState<{
        left: number;
        top: number;
        height: number;
    } | null>(null);
    React.useEffect(() => {
        if (!activePhase) {
            setAnchor(null);
            return;
        }
        const updateAnchor = () => {
            const el = railRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            setAnchor({ left: rect.right, top: rect.top, height: rect.height });
        };
        updateAnchor();
        // Re-anchor a few times during the 260ms fold animation so the flyout
        // tracks the rail's settling position.
        const t1 = setTimeout(updateAnchor, 120);
        const t2 = setTimeout(updateAnchor, 280);
        globalThis.addEventListener('resize', updateAnchor);
        globalThis.addEventListener('scroll', updateAnchor, true);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            globalThis.removeEventListener('resize', updateAnchor);
            globalThis.removeEventListener('scroll', updateAnchor, true);
        };
    }, [activePhase]);

    return (
        <div ref={railRef} className="h-full w-full flex bg-white">
            {/* Vertical icon strip */}
            <div className="w-12 h-full flex flex-col items-center pt-2 pb-3 gap-0.5 overflow-y-auto scrollbar-hide">
                {/* Expand button */}
                {onToggleFold && (
                    <button
                        type="button"
                        onClick={onToggleFold}
                        aria-label="Expand navigation"
                        title="Expand navigation"
                        className="flex items-center justify-center w-9 h-7 mb-1 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                    >
                        <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                )}
                <div className="w-7 h-px bg-gray-200 mb-1" />

                {sections.map((section) => {
                    const isImpl =
                        IMPLEMENTED_PHASES.has(section.phase) ||
                        section.items.some((i) => IMPLEMENTED_STATUSES.has(i.key));
                    const isActive = section.phase === activeRailPhase;
                    const phaseColor = PHASE_HEX[section.phase] ?? 'rgb(156,163,175)';
                    // When filtering, dim phases with zero matches so the rail still
                    // signals where matches live (matches the expanded behavior).
                    const dimmed = filterActive && section.totalCount === 0;

                    return (
                        <button
                            key={section.phase}
                            type="button"
                            onClick={() => {
                                if (isImpl) onPhaseClick(section.phase);
                            }}
                            disabled={!isImpl}
                            title={section.phase + (isImpl ? '' : ' (not implemented yet)')}
                            className={`relative w-11 h-10 flex items-center justify-center flex-shrink-0 ${
                                isImpl ? 'cursor-pointer' : 'cursor-default'
                            } ${dimmed ? 'opacity-40' : ''}`}
                        >
                            {isActive && (
                                <span
                                    className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-sm"
                                    style={{ background: phaseColor }}
                                />
                            )}
                            <span
                                className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-extrabold"
                                style={{
                                    // phaseTint() builds valid rgba(); appending alpha to rgb(...) is invalid CSS that renders
                                    // fully saturated, which made inactive look identical to active.
                                    background: isActive
                                        ? phaseColor
                                        : isImpl
                                          ? phaseTint(phaseColor, 0.1)
                                          : '#f3f4f6',
                                    color: isActive ? '#fff' : isImpl ? phaseColor : '#9ca3af',
                                    border:
                                        isImpl && !isActive
                                            ? `1px solid ${phaseTint(phaseColor, 0.25)}`
                                            : '1px solid transparent',
                                    opacity: isImpl ? 1 : 0.6
                                }}
                            >
                                {section.phase[0]}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Flyout panel — rendered via portal so it escapes the wrapper's
          overflow:hidden / rounded clip and floats over the case list. */}
            {activePhase &&
                anchor &&
                createPortal(
                    <RailFlyout
                        section={activePhase}
                        selectedKey={selectedKey}
                        filterActive={filterActive}
                        onSelect={onSelect}
                        onClose={() => onPhaseClick(activePhase.phase)}
                        anchor={anchor}
                    />,
                    document.body
                )}
        </div>
    );
}

interface RailFlyoutProps {
    section: PhaseSection;
    selectedKey: string | null;
    filterActive: boolean;
    onSelect: (key: string) => void;
    onClose: () => void;
    anchor: { left: number; top: number; height: number };
}

function RailFlyout({
    section,
    selectedKey,
    filterActive,
    onSelect,
    onClose,
    anchor
}: RailFlyoutProps) {
    const phaseColor = PHASE_HEX[section.phase] ?? 'rgb(156,163,175)';
    // Mirror the expanded behavior: in filter-active mode, hide empty steps.
    const visibleItems = filterActive ? section.items.filter((i) => i.count > 0) : section.items;

    return (
        <div
            data-wq-flyout
            className="fixed w-[260px] bg-white flex flex-col border border-gray-200 rounded-xl"
            style={{
                left: anchor.left + 6,
                top: anchor.top,
                height: anchor.height,
                zIndex: 50,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
            }}
        >
            {/* Header */}
            <div
                className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-200 flex-shrink-0"
                style={{ background: `${phaseColor}08` }}
            >
                <div
                    className="w-[3px] h-4 rounded-sm flex-shrink-0"
                    style={{ background: phaseColor }}
                />
                <span className="flex-1 text-[11px] font-bold uppercase tracking-[0.07em] text-gray-700 truncate">
                    {section.phase}
                </span>
                <span className="text-[11px] text-gray-500">
                    {section.totalCount.toLocaleString()}
                </span>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close phase panel"
                    className="ml-1 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors"
                >
                    <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Steps */}
            <div className="flex-1 overflow-y-auto">
                {visibleItems.length === 0 ? (
                    <div className="px-3 py-4 text-[11px] text-gray-400 italic">
                        {filterActive ? 'No matches in this phase.' : 'No steps configured.'}
                    </div>
                ) : (
                    visibleItems.map((item) => {
                        // Same per-step gate as the expanded tree: live when the phase is implemented or the
                        // status is whitelisted (e.g. the one built Hold status).
                        const implemented =
                            IMPLEMENTED_PHASES.has(section.phase) ||
                            IMPLEMENTED_STATUSES.has(item.key);
                        return (
                            <StepRow
                                key={item.key}
                                item={item}
                                selected={selectedKey === item.key}
                                implemented={implemented}
                                onSelect={onSelect}
                            />
                        );
                    })
                )}
            </div>

            {/* Footer hint */}
            <div className="px-3 py-1.5 border-t border-gray-100 flex-shrink-0">
                <span className="text-[10px] text-gray-400">Press Esc to dismiss</span>
            </div>
        </div>
    );
}
